/**
 * Client search (SPEC §7.7): MiniSearch over titles, bodies, tags, field values,
 * attachment captions and link titles, kept up to date incrementally; prefix filters
 * (#tag, @person, is:, date:, has:, under:) are applied on top.
 */
import MiniSearch from 'minisearch'
import { displayTitle, fieldValueText, matchesSearch, parseSearchQuery, type ItemSnapshot, type ParsedQuery } from '@terrazzo/core'
import { store } from './store'

interface Doc {
  id: string
  title: string
  body: string
  tags: string
  fields: string
  extra: string
}

const titleOf = (id: string) => store.get(id)?.title

function toDoc(it: ItemSnapshot): Doc {
  const fields = store.fields()
  return {
    id: it.id,
    title: displayTitle(it.title, titleOf),
    body: it.bodyText.slice(0, 20_000),
    tags: it.tags.map((t) => `${t} ${t.replace(/[/_-]/g, ' ')}`).join(' '),
    fields: Object.entries(it.props)
      .filter(([k]) => k !== 'view')
      .map(([k, v]) => `${fields.get(k)?.label ?? k} ${fieldValueText(v)}`)
      .join(' '),
    extra: [...it.attachments.flatMap((a) => [a.caption ?? '', a.name ?? '', a.tags.join(' ')]), it.link?.title ?? '', it.link?.siteName ?? ''].filter(Boolean).join(' '),
  }
}

let mini: MiniSearch<Doc> | null = null
const indexed = new Map<string, string>() // id -> signature
let pending = new Set<string>()
let timer: ReturnType<typeof setTimeout> | null = null

function sig(it: ItemSnapshot) {
  return `${it.updatedAt}:${it.title.length}:${it.bodyText.length}:${it.tags.join(',')}`
}

function build() {
  mini = new MiniSearch<Doc>({
    fields: ['title', 'body', 'tags', 'fields', 'extra'],
    storeFields: [],
    searchOptions: {
      boost: { title: 4, tags: 2.5, fields: 1.5, extra: 1.2, body: 1 },
      fuzzy: (term) => (term.length > 3 ? 0.2 : false),
      prefix: true,
      combineWith: 'AND',
    },
  })
  indexed.clear()
  const docs: Doc[] = []
  for (const it of store.idx.items.values()) {
    if (store.idx.tree.inTrash.has(it.id)) continue
    docs.push(toDoc(it))
    indexed.set(it.id, sig(it))
  }
  mini.addAll(docs)
}

function flush() {
  timer = null
  if (!mini) return
  const ids = pending
  pending = new Set()
  for (const id of ids) {
    const it = store.get(id)
    const visible = it && !store.idx.tree.inTrash.has(id)
    if (indexed.has(id)) {
      if (visible && indexed.get(id) === sig(it!)) continue
      mini.discard(id)
      indexed.delete(id)
    }
    if (visible) {
      mini.add(toDoc(it!))
      indexed.set(id, sig(it!))
    }
  }
}

let lastVersion = -1
store.subscribeContent(() => {
  if (!mini) return
  // Cheap: diff signatures of all items (fast, no string work unless changed).
  for (const it of store.idx.items.values()) {
    if (indexed.get(it.id) !== sig(it)) pending.add(it.id)
  }
  for (const id of indexed.keys()) if (!store.get(id)) pending.add(id)
  if (pending.size && !timer) timer = setTimeout(flush, 250)
})
store.subscribeStructure(() => {
  if (!mini || lastVersion === store.structureVersion) return
  lastVersion = store.structureVersion
  for (const id of store.idx.tree.inTrash) if (indexed.has(id)) pending.add(id)
  if (pending.size && !timer) timer = setTimeout(flush, 250)
})

export interface SearchHit {
  id: string
  score: number
}

function ensure() {
  if (!mini) build()
  else if (pending.size) flush()
}

/** The search's filters (words aside), as core reads them for the assistant too. */
function matchesFilters(it: ItemSnapshot, q: ParsedQuery, meId: string | undefined): boolean {
  return matchesSearch(it, q, { items: store.idx.items, tree: store.idx.tree, users: store.users(), fields: store.fields(), meId })
}

export function search(query: string, today: string, opts: { limit?: number; meId?: string; includeArchived?: boolean } = {}): SearchHit[] {
  ensure()
  const q = parseSearchQuery(query, today)
  const limit = opts.limit ?? 50
  const tree = store.idx.tree
  const out: SearchHit[] = []
  const allowArchived = opts.includeArchived || q.is.has('archived')
  if (q.text) {
    const res = mini!.search(q.text)
    for (const r of res) {
      const it = store.get(String(r.id))
      if (!it || it.system) continue
      if (!allowArchived && tree.inArchive.has(it.id)) continue
      if (!matchesFilters(it, q, opts.meId)) continue
      out.push({ id: it.id, score: r.score })
      if (out.length >= limit) break
    }
    // Archived matches last (still searchable, SPEC §5.1).
    if (!allowArchived && out.length < limit) {
      for (const r of res) {
        const it = store.get(String(r.id))
        if (!it || it.system || !tree.inArchive.has(it.id)) continue
        if (!matchesFilters(it, q, opts.meId)) continue
        out.push({ id: it.id, score: r.score / 10 })
        if (out.length >= limit) break
      }
    }
    return out
  }
  if (!q.tags.length && !q.people.length && !q.is.size && !q.dateFrom && !q.dateTo && !q.has.length && !q.under.length) return []
  const items = [...store.idx.items.values()].filter((it) => !it.system && !tree.inTrash.has(it.id) && (allowArchived || !tree.inArchive.has(it.id)))
  for (const it of items.sort((a, b) => b.updatedAt - a.updatedAt)) {
    if (!matchesFilters(it, q, opts.meId)) continue
    out.push({ id: it.id, score: 0 })
    if (out.length >= limit) break
  }
  return out
}

/** Fast title-only lookup for the [[ link picker and "Move to…". */
export function searchTitles(query: string, opts: { limit?: number; exclude?: Set<string>; onlyContainers?: boolean } = {}): string[] {
  ensure()
  const limit = opts.limit ?? 8
  const tree = store.idx.tree
  const q = query.trim()
  const ok = (id: string) => {
    const it = store.get(id)
    if (!it || tree.inTrash.has(id) || tree.inArchive.has(id) || opts.exclude?.has(id)) return false
    return true
  }
  if (!q) {
    return [...store.idx.items.values()]
      .filter((it) => ok(it.id) && (!opts.onlyContainers || (tree.childrenOf.get(it.id)?.length ?? 0) > 0 || tree.parentOf.get(it.id) === null))
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, limit)
      .map((i) => i.id)
  }
  return mini!
    .search(q, { fields: ['title'], prefix: true, fuzzy: 0.2, combineWith: 'AND' })
    .map((r) => String(r.id))
    .filter(ok)
    .slice(0, limit)
}
